'use client'

import { reportHandledException } from '@/lib/error-reporting'
import { useEffect, useRef, useState } from 'react'
import { PROGRESS_POLL_MS, deriveItemStageView, type ItemStageView } from './stage-progress'
import type { AudioBatchItem, AudioBatchStatusResponse } from './types'
import { TERMINAL_ITEM_STATUSES } from './types'

export const progressRowKey = (batchId: number, itemId: number) => `${batchId}:${itemId}`

export interface BatchProgressPollingOptions {
  /**
   * Comma-joined, sorted batch ids to watch. A string rather than an array so
   * the effect's dependency is value-equal — an array literal would re-subscribe
   * on every render and restart polling forever.
   */
  batchIdsKey: string
  fetchStatus: (batchId: number) => Promise<AudioBatchStatusResponse>
  /** Called once per item that reaches a terminal status, so the caller can reload. */
  onSettled?: () => void
  /** Called when polling stops because the credential is dead. */
  onAuthFailure?: (error: unknown) => void
  /** Return true for an error that means "stop polling", not "retry". */
  isFatalError?: (error: unknown) => boolean
  pollMs?: number
  /** Test seam. */
  nowMs?: () => number
}

/**
 * Polls the batch status of every in-flight row and turns it into render-ready
 * progress.
 *
 * Two things happen on each tick, and only one of them is a network call: the
 * views are ALWAYS recomputed from the last known payload against a fresh clock,
 * and a fetch is started when none is in flight. That split is what keeps the
 * line moving when the network is slow — the interpolation does not wait for a
 * response, and a slow response cannot produce a stall the user reads as a hang.
 */
export function useBatchProgressPolling({
  batchIdsKey,
  fetchStatus,
  onSettled,
  onAuthFailure,
  isFatalError,
  pollMs = PROGRESS_POLL_MS,
  nowMs = Date.now,
}: BatchProgressPollingOptions): Record<string, ItemStageView> {
  const [views, setViews] = useState<Record<string, ItemStageView>>({})

  // Callbacks are read through refs so a caller passing inline arrow functions
  // (the normal case) does not restart polling on every render.
  const callbacks = useRef({ fetchStatus, onSettled, onAuthFailure, isFatalError, nowMs })
  callbacks.current = { fetchStatus, onSettled, onAuthFailure, isFatalError, nowMs }

  useEffect(() => {
    if (!batchIdsKey) {
      setViews({})
      return
    }
    const batchIds = batchIdsKey.split(',').map(Number)
    const items = new Map<string, AudioBatchItem>()
    // The highest percentage already shown for a row, tagged with the attempt it
    // was measured under. The tag is what makes a RETRY safe: `startedAt` is
    // re-stamped every time a stage claims the row and nulled on requeue, so a
    // retried item arrives under a new tag and starts from its real position
    // instead of inheriting the number it froze at when it failed. Without the
    // tag, retrying one file while a sibling in the same batch is still running
    // leaves the retried row parked at its old high percentage until real
    // progress catches up — the exact stall this whole module exists to remove.
    const floors = new Map<string, { attempt: string | null; percent: number }>()
    const settled = new Set<string>()
    let cancelled = false
    let fetching = false
    let timer: ReturnType<typeof setInterval> | null = null

    const stop = () => {
      cancelled = true
      if (timer) clearInterval(timer)
      timer = null
    }

    const repaint = () => {
      const now = callbacks.current.nowMs()
      const next: Record<string, ItemStageView> = {}
      for (const [key, item] of items) {
        const view = deriveItemStageView(item, now)
        if (view.percent === null) {
          next[key] = view
          continue
        }
        const floor = floors.get(key)
        const sameAttempt = floor !== undefined && floor.attempt === item.startedAt
        if (sameAttempt && floor.percent > view.percent) {
          next[key] = { ...view, percent: floor.percent }
        } else {
          floors.set(key, { attempt: item.startedAt, percent: view.percent })
          next[key] = view
        }
      }
      setViews(next)
    }

    const poll = async () => {
      if (cancelled || fetching) return
      fetching = true
      try {
        const responses = await Promise.all(batchIds.map((id) => callbacks.current.fetchStatus(id)))
        if (cancelled) return
        let anySettled = false
        for (const response of responses) {
          for (const item of response.items) {
            const key = progressRowKey(response.id, item.id)
            items.set(key, item)
            if (TERMINAL_ITEM_STATUSES.has(item.status) && !settled.has(key)) {
              settled.add(key)
              anySettled = true
            }
          }
        }
        repaint()
        if (anySettled) callbacks.current.onSettled?.()
      } catch (error) {
        reportHandledException(error, {
          source: 'features/batch-transcription/use-batch-progress-polling.ts',
        })
        if (cancelled) return
        // A dead credential must not spin forever on a stale token; everything
        // else (a blip, a context that self-heals) keeps retrying on the next tick.
        if (callbacks.current.isFatalError?.(error)) {
          // Stop the timer too, not just this pass: a dead credential produces
          // the same failure forever, and retrying it every 2s is a request
          // storm that never recovers.
          stop()
          callbacks.current.onAuthFailure?.(error)
          return
        }
        console.warn('[transcriptions] progress poll failed; retrying on the next tick', error)
      } finally {
        fetching = false
      }
    }

    void poll()
    timer = setInterval(() => {
      // Repaint first: interpolation is what makes the line move between
      // samples, and it must not be gated on the request succeeding.
      repaint()
      void poll()
    }, pollMs)

    return stop
  }, [batchIdsKey, pollMs])

  return views
}
